"use client";

import * as React from "react";

type ThemeProviderProps = {
  children: React.ReactNode;
  attribute?: string;
  defaultTheme?: string;
  enableSystem?: boolean;
  disableTransitionOnChange?: boolean;
};

export function ThemeProvider({
  children,
  attribute = "class",
  defaultTheme = "light",
  enableSystem = true,
  disableTransitionOnChange = false,
}: ThemeProviderProps) {
  const [theme, setTheme] = React.useState<string | undefined>(undefined);
  
  // Use effect to handle theme initialization and changes
  React.useEffect(() => {
    const root = window.document.documentElement;
    const initialColorValue = defaultTheme;
    
    // Set theme based on localStorage or system preference
    const storedTheme = localStorage.getItem("theme");
    let resolvedTheme = storedTheme || initialColorValue;
    
    if (enableSystem && !storedTheme) {
      const systemPreference = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
      resolvedTheme = systemPreference;
    }
    
    setTheme(resolvedTheme);
    
    // Apply theme to document
    if (resolvedTheme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    
    // Listen for system preference changes
    if (enableSystem) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => {
        if (!localStorage.getItem("theme")) {
          setTheme(mediaQuery.matches ? "dark" : "light");
          if (mediaQuery.matches) {
            root.classList.add("dark");
          } else {
            root.classList.remove("dark");
          }
        }
      };
      
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [defaultTheme, enableSystem]);
  
  // Function to set theme
  const setThemeValue = React.useCallback((newTheme: string) => {
    const root = window.document.documentElement;
    localStorage.setItem("theme", newTheme);
    
    if (disableTransitionOnChange) {
      root.classList.add("disable-transitions");
    }
    
    if (newTheme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    
    setTheme(newTheme);
    
    if (disableTransitionOnChange) {
      window.setTimeout(() => {
        root.classList.remove("disable-transitions");
      }, 0);
    }
  }, [disableTransitionOnChange]);
  
  const providerValue = React.useMemo(
    () => ({
      theme,
      setTheme: setThemeValue,
    }),
    [theme, setThemeValue]
  );
  
  return (
    <ThemeContext.Provider value={providerValue}>
      {children}
    </ThemeContext.Provider>
  );
}

// Create context for theme
type ThemeContextType = {
  theme: string | undefined;
  setTheme: (theme: string) => void;
};

const ThemeContext = React.createContext<ThemeContextType | undefined>(
  undefined
);

// Hook to use theme
export const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  
  return context;
}; 